// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { IModalStyles, Modal, Stack } from '@fluentui/react';
import { useCallback, useState } from 'react';
import { uploadFeedback, createFeedback } from '../../feedbacks/Feedback';
import { Constants } from '../../constants';
import { FeedbackDialogHeader } from './FeedbackDialog.header';
import { FeedbackDialogBody } from './FeedbackDialog.body';
import { FeedbackDialogFooter } from './FeedbackDialog.footer';
import { useFunctionsAppToken, useUserAlias } from 'hooks/useUser';

declare const CALLING_SDK_VERSION: string;
declare const CHAT_SDK_VERSION: string;
declare const COMMON_SDK_VERSION: string;
declare const COMMUNICATION_REACT_SDK_VERSION: string;

type FeedbackPopupProps = {
  onDismiss: () => void;
  resetFields: () => void;
  callId?: string;
  isOpen: boolean;
  defaultFields?: {
    title?: string;
    feedbackType?: string;
    comment?: string;
  };
};

export const FeedbackPopup = (props: FeedbackPopupProps): JSX.Element => {
  const [feedbackType, setFeedbackType] = useState<string>(props.defaultFields?.feedbackType ?? 'Calling - Audio');
  const [title, setTitle] = useState<string | undefined>(props.defaultFields?.title);
  const [comment, setComment] = useState<string | undefined>(props.defaultFields?.comment);
  const [guid, setGuid] = useState<string | undefined>();
  const [screenshotResult, setScreenshotResult] = useState<Blob | null>(null);
  const [submittingFeedback, setSubmittingFeedback] = useState<boolean>(false);
  const userAlias = useUserAlias();
  const functionsAppToken = useFunctionsAppToken();

  const submitFeedback = useCallback(async () => {
    setSubmittingFeedback(true);
    const feedback = createFeedback(
      parseGroupId() ?? '(no acs group id found)',
      userAlias ?? '(no user alias found)',
      title ?? 'ACS Selfhost Feedback - no title set',
      feedbackType,
      (comment ?? defaultComment()).concat(feedbackAddendum(props.callId, !!screenshotResult))
    );
    try {
      await uploadFeedback(feedback, screenshotResult, functionsAppToken ?? '');
      setGuid(feedback.feedbackId);
    } catch (e) {
      alert('Failed to submit feedback. Please try again later.');
      throw e;
    } finally {
      setSubmittingFeedback(false);
    }
  }, [title, feedbackType, comment, props.callId, functionsAppToken, screenshotResult, setSubmittingFeedback]);

  const isValidFeedback = (): boolean => {
    return !title;
  };

  const dismiss = (): void => {
    // If feedback was submitted, when the user dismisses the dialog, we want to reset the form
    if (guid) {
      props.resetFields();
    }
    props.onDismiss();
  };

  const [dialogHidden, setDialogHidden] = useState<boolean>(false);
  const hideDialog = (): void => setDialogHidden(true);
  const showDialog = (): void => setDialogHidden(false);

  return (
    <Modal isOpen={props.isOpen} onDismiss={dismiss} styles={feedbackDialogContainerStyles(dialogHidden)}>
      <Stack tokens={{ childrenGap: '1rem' }}>
        <Stack.Item>
          <FeedbackDialogHeader feedbackSubmitted={!!guid} />
        </Stack.Item>
        <Stack.Item>
          <FeedbackDialogBody
            guid={guid}
            defaultFeedbackType={feedbackType}
            comment={comment ?? defaultComment()}
            title={title}
            setTitle={setTitle}
            setComment={setComment}
            setFeedbackType={setFeedbackType}
            onScreenshotResultChanged={(screenshotResult) => setScreenshotResult(screenshotResult)}
            onBeforeScreenshot={hideDialog}
            onAfterScreenshot={showDialog}
            onScreenshotFailed={showDialog}
          />
        </Stack.Item>
        <Stack.Item>
          <FeedbackDialogFooter
            guid={guid}
            submitDisabled={isValidFeedback()}
            submittingFeedback={submittingFeedback}
            onSubmit={submitFeedback}
            onClose={dismiss}
          />
        </Stack.Item>
      </Stack>
    </Modal>
  );
};

const feedbackDialogContainerStyles = (isHidden: boolean): Partial<IModalStyles> => ({
  root: {
    visibility: isHidden ? 'hidden' : 'visible'
  },
  scrollableContent: {
    padding: '2rem',
    maxHeight: '80vh'
  }
});

const parseGroupId = (): string | null => {
  const groupId = new URLSearchParams(window.location.search).get(Constants.ACS_CALL_PARAM_NAME);
  return groupId;
};

const defaultComment = (): string => `Expectation: <what are you expecting?>

Actual: <what did you really see?>

Repro steps (if applicable):
1.
2.
3.
4.
5.`;

/** This is appended to the feedback logged. */
const feedbackAddendum = (callId: string | undefined, screenshotAttached: boolean): string => `
Screenshot Attached? (yes/no): ${screenshotAttached ? 'yes' : 'no'}

CallId: ${callId ?? 'No call id available'}
CFV Link: ${
  callId
    ? `https://dataexplorer.azure.com/dashboards/147caf74-a1e1-4018-9a0b-5989208c9ec3?p-_startTime=1hours&p-_endTime=now&p-CALL_ID=${callId}`
    : 'No CFV link available'
}

@azure/communication-calling: ${CALLING_SDK_VERSION}
@azure/communication-chat: ${CHAT_SDK_VERSION}
@azure/communication-common: ${COMMON_SDK_VERSION}
@azure/communication-react: ${COMMUNICATION_REACT_SDK_VERSION}
`;
